import TestimonialCard from '@/Components/Homepage/TestimonialCard';
import { Swiper, SwiperSlide, useSwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from "swiper/types";
import { useEffect, useRef, useState } from 'react';
import FillDots from '@/Components/Svg/FillDots';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppContext } from '@/Components/Providers/AppProvider';

export type TestimonialData = {
    type: string,
    username: string,
    profile_url: string,
    profile_picture_url: string,
    text: string,
};

type Props = {
    testimonials: TestimonialData[],
};

export default function Testimonials({ testimonials }: Props) {
    const [isSwiping, setIsSwiping] = useState(false);
    const [dotsWidth, setDotsWidth] = useState(0);
    const [dotsHeight, setDotsHeight] = useState(0);
    const [swiper, setSwiper] = useState<SwiperType | null>(null);
    const { homepageAnchors: { testimonials: sectionRef }} = useAppContext();

    const TestimonialSlide = ({ username, text, type, profileUrl, avatarUrl }: {
        username: string,
        text: string,
        type: string,
        profileUrl: string,
        avatarUrl: string,
    }) => {
        const slide = useSwiperSlide();
        return <TestimonialCard name={`@${username}`}
            title={type}
            text={text}
            profileUrl={profileUrl}
            avatarUrl={avatarUrl}
            dimmed={!slide.isActive}
        />
    }

    useEffect(() => {
        if (sectionRef.current) {
            setDotsWidth(sectionRef.current.getBoundingClientRect().width);
            setDotsHeight(sectionRef.current.getBoundingClientRect().height);
        }
    }, [dotsWidth, dotsHeight, sectionRef]);

    return <section id="testimonials" ref={sectionRef} className="relative w-full max-w-dvw overflow-hidden">
        <div className="relative py-16 space-y-16 my-8 mask-(--mask-grid-edges-horizontal-large)">
            <FillDots width={sectionRef.current?.getBoundingClientRect().width ?? 0} height={sectionRef.current?.getBoundingClientRect().height ?? 0} density={Math.floor((sectionRef.current?.getBoundingClientRect().height ?? 0) / 16)} size={2} color="#F57C00" className="absolute top-0 left-0 z-1" />
            <div className="relative text-center w-full md:w-max mx-auto uppercase tracking-widest space-y-1 bg-light shadow-[0px_0px_32px_16px_var(--color-light)] p-4 z-10">
                <h1 className="text-xl md:text-3xl font-medium">Everyone's Loving Our Share</h1>
                <h2 className="text-base md:text-xl">Hear what our supporters have to say.</h2>
            </div>
            <div className="relative w-full h-full max-w-dvw">
                <Swiper
                    onSwiper={(swiper) => setSwiper(swiper)}
                    slidesPerView={1}
                    breakpoints={{
                        768: {
                            slidesPerView: 3,
                            spaceBetween: 32,
                        },
                    }}
                    direction="horizontal"
                    onTouchStart={() => setIsSwiping(true)}
                    onTouchEnd={() => setIsSwiping(false)}
                    onClick={swiper => swiper.clickedIndex === swiper.activeIndex ? (void 0) : (swiper.clickedIndex > swiper.activeIndex ? swiper.slideNext() : swiper.slidePrev())}
                    className={mc("w-[95%]", { 'cursor-grabbing': isSwiping, 'cursor-pointer': !isSwiping })}
                    style={{
                        overflow: 'visible'
                    }}
                    spaceBetween={16}
                    centeredSlides={true}
                    loop={true}
                >
                    {testimonials.map((testimonial, index) => <SwiperSlide key={`${index}_${testimonial.username}`}>
                        <TestimonialSlide
                            username={testimonial.username}
                            profileUrl={testimonial.profile_url}
                            avatarUrl={testimonial.profile_picture_url}
                            text={testimonial.text}
                            type={testimonial.type}
                        />
                    </SwiperSlide>)}
                </Swiper>
            </div>
            <div className="flex justify-center items-center w-full">
                <div className="flex justify-between items-center gap-8 bg-light px-4 py-2 rounded-md z-20">
                    <button onClick={() => swiper ? swiper.slidePrev() : void 0}
                        className="relative group h-full flex justify-center items-center col-span-2 gap-2 cursor-pointer transition-colors z-20"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} size="sm" className="text-brand" />
                        <div className="relative overflow-hidden font-bold uppercase text-md/[1.5rem] whitespace-break-spaces">
                            <span className="block group-hover:-translate-y-full transition-all ease-in-out duration-250">
                                Prev
                            </span>
                            <span className="absolute top-0 left-0 translate-y-full group-hover:translate-none transition-all ease-in-out duration-250">
                                Prev
                            </span>
                        </div>
                    </button>
                    <div className="relative w-[2px] h-4 bg-brand"></div>
                    <button onClick={() => swiper ? swiper.slideNext() : void 0}
                        className="relative group h-full flex justify-center items-center col-span-2 gap-2 cursor-pointer transition-colors z-20">
                        <div className="relative overflow-hidden font-bold uppercase text-md/[1.5rem] whitespace-break-spaces">
                            <span className="block group-hover:-translate-y-full transition-all ease-in-out duration-250">
                                Next
                            </span>
                            <span className="absolute top-0 left-0 translate-y-full group-hover:translate-none transition-all ease-in-out duration-250">
                                Next
                            </span>
                        </div>
                        <FontAwesomeIcon icon={faChevronRight} size="sm" className="text-brand" />
                    </button>
                </div>
            </div>
        </div>
    </section>
}
